import { useCallback, useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

const PAGE_SIZE = 25

const getErrorMessage = (error, fallbackMessage) =>
    error?.response?.body?.detail || error?.response?.body?.message || fallbackMessage

const buildFilterObject = (companyIdentifier) => ({ CPFCNPJ: companyIdentifier })

export const getFieldErrors = (error, fields = []) => {
    const fieldNames = new Set(fields.map((field) => field.name))
    const responseBody = error?.response?.body

    if (!responseBody || typeof responseBody !== "object") return {}

    return Object.entries(responseBody).reduce((fieldErrors, [fieldName, messages]) => {
        if (!fieldNames.has(fieldName)) return fieldErrors

        const message = Array.isArray(messages) ? messages.filter(Boolean).join(" ") : messages
        if (typeof message !== "string" || !message.trim()) return fieldErrors

        return { ...fieldErrors, [fieldName]: message }
    }, {})
}

const useCompanyRegistrationTable = ({ definition, companyIdentifier, onSaved }) => {
    const serviceRef = useRef(new DatalakeService())
    const requestIdRef = useRef(0)
    const mutationRef = useRef(false)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)
    const [loading, setLoading] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [isMutating, setIsMutating] = useState(false)

    const loadPage = useCallback(async ({ nextPage = page, nextPageSize = pageSize } = {}) => {
        if (!companyIdentifier || !definition?.endpoint) {
            setRows([])
            setRowCount(0)
            setHasLoaded(true)
            setError(null)
            return
        }

        const requestId = requestIdRef.current + 1
        requestIdRef.current = requestId
        setLoading(true)
        setHasLoaded(false)
        setError(null)

        try {
            const response = await serviceRef.current.loadPaginatedTable(definition.endpoint, {
                filters: buildFilterObject(companyIdentifier),
                limit: nextPageSize,
                offset: (nextPage - 1) * nextPageSize,
            })
            if (requestId !== requestIdRef.current) return

            const body = response?.body ?? {}
            setRows((body.results ?? []).map((item) => ({ ...item, id: item.ID })))
            setRowCount(body.count ?? 0)
            setPage(nextPage)
            setPageSize(nextPageSize)
            setHasLoaded(true)
        } catch (error) {
            if (requestId !== requestIdRef.current) return
            setRows([])
            setRowCount(0)
            setHasLoaded(true)
            const message = getErrorMessage(error, `Não foi possível carregar ${definition.title}.`)
            setError(message)
            dispatchSnackbar(message, "error")
        } finally {
            if (requestId === requestIdRef.current) setLoading(false)
        }
    }, [companyIdentifier, definition, page, pageSize])

    useEffect(() => {
        setPage(1)
        setPageSize(PAGE_SIZE)
        setRows([])
        setRowCount(0)
        setHasLoaded(false)
        setError(null)
        loadPage({ nextPage: 1, nextPageSize: PAGE_SIZE })

        return () => {
            requestIdRef.current += 1
        }
    }, [companyIdentifier, definition?.endpoint])

    const save = useCallback(async ({ row, isNew }) => {
        if (mutationRef.current) return false
        mutationRef.current = true
        setIsMutating(true)

        try {
            if (isNew) {
                await serviceRef.current.createTableEndpoint(definition.endpoint, row)
                dispatchSnackbar("Registro adicionado com sucesso.", "success")
            } else {
                await serviceRef.current.updateTableEndpoint(definition.endpoint, row.ID, row)
                dispatchSnackbar("Registro atualizado com sucesso.", "success")
            }

            await loadPage()
            onSaved?.()
            return true
        } catch (error) {
            const fieldErrors = getFieldErrors(error, definition.fields)
            if (Object.keys(fieldErrors).length > 0) return { fieldErrors }

            dispatchSnackbar(getErrorMessage(error, "Não foi possível salvar o registro."), "error")
            return false
        } finally {
            mutationRef.current = false
            setIsMutating(false)
        }
    }, [definition.endpoint, loadPage, onSaved])

    const remove = useCallback(async (row) => {
        if (!row?.ID || mutationRef.current) return false
        mutationRef.current = true
        setIsMutating(true)

        try {
            await serviceRef.current.deleteTableEndpoint(definition.endpoint, row.ID)
            dispatchSnackbar("Registro excluído com sucesso.", "success")
            const nextPage = rows.length === 1 && page > 1 ? page - 1 : page
            await loadPage({ nextPage })
            onSaved?.()
            return true
        } catch (error) {
            dispatchSnackbar(getErrorMessage(error, "Não foi possível excluir o registro."), "error")
            return false
        } finally {
            mutationRef.current = false
            setIsMutating(false)
        }
    }, [definition.endpoint, loadPage, onSaved, page, rows.length])

    return {
        rows,
        rowCount,
        page,
        pageSize,
        loading,
        hasLoaded,
        error,
        isMutating,
        loadPage,
        save,
        remove,
    }
}

export default useCompanyRegistrationTable
